// OpportunityCardActionMenu — shared hover-revealed action menu for
// Grid (FE-PINV-069), Kanban (FE-PINV-070), and List (FE-PINV-071).
//
// Owns five actions per spec:
//   - Open → navigates to OpportunityDetailPage
//   - Log Touchpoint → opens ActivityComposerModal with the bound
//     opportunity_id and default kind=meeting
//   - Move stage → one-step menu of valid forward stages
//   - Mark won → confirmation modal (today + optional final value)
//   - Mark lost → confirmation modal (BE-SCRM-026 lost_reason +
//     optional lost_to_broker + notes)
//
// Move-stage forward-stage gate mirrors BE-SCRM-004's transition
// rules: skip-ahead is allowed (lead → proposal is fine), backward
// is not; the BOR-only stage surfaces only for bor_takeover
// opportunities.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "@/api/client";
import { Button, Modal } from "@/design-system";

import { ActivityComposerModal } from "@/features/sales/activities/ActivityComposerModal";
import { useCreateActivityMutation } from "@/features/sales/activities/hooks";
import type { ActivityCreateInput } from "@/features/sales/activities/types";

import { useMoveOpportunityStageMutation } from "@/features/sales/RenewalBoard/hooks";
import type {
  OpportunityStage,
  OpportunityType,
} from "@/features/sales/RenewalBoard/types";

import { MoneyInput } from "./MoneyInput";


const STAGE_ORDER: OpportunityStage[] = [
  "lead",
  "meeting_set",
  "qualified",
  "proposal",
  "negotiation",
  "bor_signed_pending_effective_date",
  "closed_won",
  "closed_lost",
];


const STAGE_LABEL: Record<OpportunityStage, string> = {
  lead: "Lead",
  meeting_set: "Meeting set",
  qualified: "Qualified",
  proposal: "Proposal",
  negotiation: "Negotiation",
  bor_signed_pending_effective_date: "BOR signed",
  closed_won: "Closed won",
  closed_lost: "Closed lost",
};


const LOST_REASONS: { value: string; label: string }[] = [
  { value: "incumbent_retained", label: "Incumbent retained" },
  { value: "lost_to_named_broker", label: "Lost to named broker" },
  { value: "price", label: "Price" },
  { value: "no_decision", label: "No decision" },
  { value: "account_dissolved", label: "Account dissolved" },
  { value: "account_acquired_by_parent", label: "Account acquired by parent" },
  { value: "service_concern", label: "Service concern" },
  { value: "relationship_concern", label: "Relationship concern" },
  { value: "compliance_concern", label: "Compliance concern" },
  { value: "coverage_gap_concern", label: "Coverage gap concern" },
  { value: "other", label: "Other" },
];


export function validForwardStages(
  current: OpportunityStage,
  opp_type: OpportunityType,
): OpportunityStage[] {
  if (current === "closed_won" || current === "closed_lost") return [];
  const currentIdx = STAGE_ORDER.indexOf(current);
  return STAGE_ORDER.filter((s, idx) => {
    if (s === current) return false;
    if (idx <= currentIdx) return false;
    if (
      s === "bor_signed_pending_effective_date"
      && opp_type !== "bor_takeover"
    ) {
      return false;
    }
    return true;
  });
}


export interface OpportunityCardActionMenuProps {
  opportunity_id: string;
  prospect_name: string;
  stage: OpportunityStage;
  opportunity_type: OpportunityType;
  /** Used as the default for the Mark Won modal. Pass null when
   * unknown. */
  value_estimate: number | null;
  /** Override the Open behavior. Defaults to react-router navigate to
   * /dashboard/sales-crm/opportunities/{id}. */
  onOpen?: (opportunity_id: string) => void;
  className?: string;
}


type ModalKind =
  | null
  | "move_stage"
  | "mark_won"
  | "mark_lost"
  | "log_touchpoint";


export function OpportunityCardActionMenu({
  opportunity_id, prospect_name, stage, opportunity_type,
  value_estimate, onOpen, className,
}: OpportunityCardActionMenuProps): JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState<ModalKind>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function handleOpen() {
    setOpen(false);
    if (onOpen) {
      onOpen(opportunity_id);
    } else {
      navigate(`/dashboard/sales-crm/opportunities/${opportunity_id}`);
    }
  }

  function handleAction(kind: ModalKind) {
    setOpen(false);
    setModal(kind);
  }

  return (
    <div
      ref={ref}
      data-testid={`opp-action-menu-${opportunity_id}`}
      className={"relative inline-block " + (className ?? "")}
    >
      <button
        type="button"
        data-testid={`opp-action-trigger-${opportunity_id}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Actions for ${prospect_name}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className={[
          "inline-flex h-6 w-6 items-center justify-center rounded",
          "text-text-tertiary hover:bg-recessed hover:text-text-primary",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-teal",
        ].join(" ")}
      >
        ⋯
      </button>

      {open && (
        <div
          role="menu"
          data-testid={`opp-action-list-${opportunity_id}`}
          className={[
            "absolute right-0 z-20 mt-1 min-w-[11rem] rounded-md",
            "border border-border-subtle bg-raised py-1 shadow-lg",
          ].join(" ")}
        >
          <MenuItem
            testid="opp-action-open"
            label="Open"
            onClick={handleOpen}
          />
          <MenuItem
            testid="opp-action-log-touchpoint"
            label="Log Touchpoint"
            onClick={() => handleAction("log_touchpoint")}
          />
          <MenuItem
            testid="opp-action-move-stage"
            label="Move stage"
            onClick={() => handleAction("move_stage")}
            disabled={
              stage === "closed_won" || stage === "closed_lost"
            }
          />
          <MenuItem
            testid="opp-action-mark-won"
            label="Mark won"
            onClick={() => handleAction("mark_won")}
            disabled={stage === "closed_won"}
          />
          <MenuItem
            testid="opp-action-mark-lost"
            label="Mark lost"
            destructive
            onClick={() => handleAction("mark_lost")}
            disabled={stage === "closed_lost"}
          />
        </div>
      )}

      {modal === "move_stage" && (
        <MoveStageModal
          opportunity_id={opportunity_id}
          current_stage={stage}
          opportunity_type={opportunity_type}
          on_close={() => setModal(null)}
        />
      )}

      {modal === "mark_won" && (
        <MarkWonModal
          opportunity_id={opportunity_id}
          current_value={value_estimate}
          on_close={() => setModal(null)}
        />
      )}

      {modal === "mark_lost" && (
        <MarkLostModal
          opportunity_id={opportunity_id}
          on_close={() => setModal(null)}
        />
      )}

      {modal === "log_touchpoint" && (
        <LogTouchpointHost
          opportunity_id={opportunity_id}
          on_close={() => setModal(null)}
        />
      )}
    </div>
  );
}


function MenuItem({
  testid, label, onClick, destructive, disabled,
}: {
  testid: string;
  label: string;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testid}
      onClick={() => {
        if (disabled) return;
        onClick();
      }}
      disabled={disabled}
      className={[
        "block w-full px-3 py-1.5 text-left text-xs",
        "hover:bg-recessed disabled:cursor-not-allowed disabled:opacity-50",
        destructive
          ? "text-ember-ink hover:bg-ember/10"
          : "text-text-primary",
      ].join(" ")}
    >
      {label}
    </button>
  );
}


// ── Move stage modal ─────────────────────────────────────────────


function MoveStageModal({
  opportunity_id, current_stage, opportunity_type, on_close,
}: {
  opportunity_id: string;
  current_stage: OpportunityStage;
  opportunity_type: OpportunityType;
  on_close: () => void;
}) {
  const forwards = validForwardStages(current_stage, opportunity_type);
  const move = useMoveOpportunityStageMutation();

  return (
    <Modal
      open
      onClose={on_close}
      title={`Move "${STAGE_LABEL[current_stage]}" forward`}
      size="sm"
    >
      <div data-testid="move-stage-modal" className="flex flex-col gap-2">
        <p className="text-xs text-text-secondary">
          Pick a forward stage. Backward moves require the detail page.
        </p>
        {forwards.length === 0 ? (
          <p className="text-xs italic text-text-tertiary">
            No forward stages available.
          </p>
        ) : (
          forwards.map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`move-stage-option-${s}`}
              onClick={() => {
                move.mutate(
                  { opportunity_id, to_stage: s },
                  { onSuccess: on_close },
                );
              }}
              disabled={move.isPending}
              className={[
                "rounded-md border border-border-subtle px-3 py-2",
                "text-left text-xs text-text-primary hover:bg-recessed",
                "disabled:opacity-50",
              ].join(" ")}
            >
              {STAGE_LABEL[s]}
            </button>
          ))
        )}
      </div>
    </Modal>
  );
}


// ── Mark Won modal ───────────────────────────────────────────────


function MarkWonModal({
  opportunity_id, current_value, on_close,
}: {
  opportunity_id: string;
  current_value: number | null;
  on_close: () => void;
}) {
  const move = useMoveOpportunityStageMutation();
  const initial_bi = current_value !== null && current_value !== undefined
    ? BigInt(current_value)
    : null;
  const [value_cents, setValueCents] = useState<bigint | null>(initial_bi);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setError(null);
    setSubmitting(true);
    try {
      if (value_cents !== null && value_cents !== initial_bi) {
        await api.patch(
          `/api/v1/sales/opportunities/${opportunity_id}`,
          { estimated_annual_revenue_cents: Number(value_cents) },
        );
      }
      await new Promise<void>((resolve, reject) => {
        move.mutate(
          { opportunity_id, to_stage: "closed_won" },
          {
            onSuccess: () => resolve(),
            onError: (err) => reject(err),
          },
        );
      });
      on_close();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Couldn't mark this opportunity as won.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  const today_label = new Date().toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "numeric",
  });

  return (
    <Modal
      open
      onClose={on_close}
      title="Mark as Closed Won"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={on_close}>Cancel</Button>
          <Button
            data-testid="mark-won-confirm"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? "Saving…" : "Mark won"}
          </Button>
        </>
      }
    >
      <div
        data-testid="mark-won-modal"
        className="flex flex-col gap-3 text-sm"
      >
        <p className="text-text-secondary">
          The Closed Won timestamp will be stamped at{" "}
          <strong className="text-text-primary">{today_label}</strong>.
        </p>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-text-secondary">
            Final estimated value (optional)
          </span>
          <MoneyInput
            value={value_cents}
            onChange={setValueCents}
            placeholder="0"
            id="mark-won-value"
          />
        </label>
        {error && (
          <p className="text-xs text-ember-ink">{error}</p>
        )}
      </div>
    </Modal>
  );
}


// ── Mark Lost modal ──────────────────────────────────────────────


function MarkLostModal({
  opportunity_id, on_close,
}: {
  opportunity_id: string;
  on_close: () => void;
}) {
  const move = useMoveOpportunityStageMutation();
  const [reason, setReason] = useState<string>("");
  const [lost_to_broker, setLostToBroker] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const requires_broker = reason === "lost_to_named_broker";

  function handleConfirm() {
    setError(null);
    if (!reason) {
      setError("Pick a loss reason.");
      return;
    }
    if (requires_broker && !lost_to_broker.trim()) {
      setError("Name the broker who took the account.");
      return;
    }
    move.mutate(
      {
        opportunity_id,
        to_stage: "closed_lost",
        lost_reason: reason,
        lost_to_broker: requires_broker ? lost_to_broker.trim() : null,
        lost_notes: notes.trim() || null,
      },
      {
        onSuccess: on_close,
        onError: (err) => setError(err.message),
      },
    );
  }

  return (
    <Modal
      open
      onClose={on_close}
      title="Mark as Closed Lost"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={on_close}>Cancel</Button>
          <Button
            data-testid="mark-lost-confirm"
            onClick={handleConfirm}
            disabled={move.isPending}
          >
            {move.isPending ? "Saving…" : "Mark lost"}
          </Button>
        </>
      }
    >
      <div
        data-testid="mark-lost-modal"
        className="flex flex-col gap-3 text-sm"
      >
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-text-secondary">
            Reason
          </span>
          <select
            data-testid="mark-lost-reason-select"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className={[
              "rounded-md border border-border-subtle bg-raised",
              "px-2 py-1.5 text-xs text-text-primary",
            ].join(" ")}
          >
            <option value="">Select reason…</option>
            {LOST_REASONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </label>

        {requires_broker && (
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-text-secondary">
              Lost to which broker?
            </span>
            <input
              type="text"
              data-testid="mark-lost-to-broker-input"
              value={lost_to_broker}
              onChange={(e) => setLostToBroker(e.target.value)}
              className={[
                "rounded-md border border-border-subtle bg-raised",
                "px-2 py-1.5 text-xs text-text-primary",
              ].join(" ")}
            />
          </label>
        )}

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-text-secondary">
            Notes (optional)
          </span>
          <textarea
            data-testid="mark-lost-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className={[
              "rounded-md border border-border-subtle bg-raised",
              "px-2 py-1.5 text-xs text-text-primary",
            ].join(" ")}
          />
        </label>

        {error && <p className="text-xs text-ember-ink">{error}</p>}
      </div>
    </Modal>
  );
}


// ── Log Touchpoint host (mounts ActivityComposerModal) ───────────


function LogTouchpointHost({
  opportunity_id, on_close,
}: {
  opportunity_id: string;
  on_close: () => void;
}) {
  const create = useCreateActivityMutation();
  function handleSubmit(input: ActivityCreateInput) {
    create.mutate(input, { onSuccess: on_close });
  }
  return (
    <ActivityComposerModal
      initial_type="meeting"
      bound_opportunity_id={opportunity_id}
      is_submitting={create.isPending}
      on_submit={handleSubmit}
      on_cancel={on_close}
    />
  );
}
